import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle } from '@mui/icons-material';

import {
  NumberBadge,
  ParsingStepRow,
  ParsingStepsList,
  ParsingStepText,
  StepIconBox,
  StepSpinner,
} from '@internal/components/ai-call/ai-call.styled';

const NORMAL_STEP_DURATION = 7000;
const FAST_STEP_DURATION = 2000;

type StepStatus = 'idle' | 'spinning' | 'done';

type ProcessingLoaderProps = {
  steps: readonly string[];
  isDone: boolean;
  onComplete?: () => void;
  /** Compact variant: drop the row borders / active outline. */
  borderless?: boolean;
};

type StepIconProps = {
  status: StepStatus;
  index: number;
};

const StepIcon = ({ status, index }: StepIconProps) => {
  if (status === 'done') {
    return <CheckCircle color="success" sx={{ fontSize: 24 }} />;
  }
  if (status === 'spinning') {
    return <StepSpinner size={22} thickness={3} />;
  }
  return <NumberBadge>{index + 1}</NumberBadge>;
};

type ProcessingStepProps = {
  label: string;
  status: StepStatus;
  index: number;
  borderless?: boolean;
};

const ProcessingStep = ({
  label,
  status,
  index,
  borderless,
}: ProcessingStepProps) => (
  <ParsingStepRow $status={status} $borderless={borderless}>
    <StepIconBox>
      <StepIcon status={status} index={index} />
    </StepIconBox>
    <ParsingStepText variant="body1" $status={status}>
      {label}
    </ParsingStepText>
  </ParsingStepRow>
);

const ProcessingLoader = ({
  steps,
  isDone,
  onComplete,
  borderless,
}: ProcessingLoaderProps) => {
  const [statuses, setStatuses] = useState<StepStatus[]>(
    steps.map((_, i) => (i === 0 ? 'spinning' : 'idle'))
  );
  const isDoneRef = useRef(isDone);
  isDoneRef.current = isDone;

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const currentStepRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const advanceStep = useCallback(() => {
    const current = currentStepRef.current;
    const isLast = current === steps.length - 1;

    setStatuses(prev => {
      const next = [...prev];
      next[current] = 'done';
      if (!isLast) next[current + 1] = 'spinning';
      return next;
    });

    if (isLast) {
      onCompleteRef.current?.();
      return;
    }

    currentStepRef.current = current + 1;
    const nextIsLast = currentStepRef.current === steps.length - 1;

    if (nextIsLast && !isDoneRef.current) {
      return;
    }

    const delay = isDoneRef.current ? FAST_STEP_DURATION : NORMAL_STEP_DURATION;
    timerRef.current = setTimeout(advanceStep, delay);
  }, [steps.length]);

  useEffect(() => {
    timerRef.current = setTimeout(advanceStep, NORMAL_STEP_DURATION);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [advanceStep]);

  useEffect(() => {
    if (!isDone) return;
    const isOnLastStep = currentStepRef.current === steps.length - 1;

    if (isOnLastStep) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(advanceStep, FAST_STEP_DURATION);
    }
  }, [isDone, advanceStep, steps.length]);

  return (
    <ParsingStepsList>
      {steps.map((label, i) => (
        <ProcessingStep
          key={label}
          label={label}
          status={statuses[i]}
          index={i}
          borderless={borderless}
        />
      ))}
    </ParsingStepsList>
  );
};

export default ProcessingLoader;
